import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Typography, Button } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import { useTheme } from '../../ThemeContext';
import filmhookEmblem from '../../assets/Filmhook_Apps_Logoonly.png';

// Dynamic Filmhook Platform Telemetry Stages
const FILMHOOK_TELEMETRY = [
  {
    tag: 'AUDITIONS & CASTING',
    icon: '🎭',
    metric: '14,820+ Verified Actors Online',
    detail: 'Direct Video Casting & Talent Directory',
  },
  {
    tag: 'NEURAL AI STUDIO',
    icon: '🤖',
    metric: 'Script-to-Storyboard Diffusion',
    detail: '2.39:1 Anamorphic Shotlist Generation',
  },
  {
    tag: 'CLOUD NLE TIMELINE',
    icon: '✂️',
    metric: 'Multi-Track 4K DCI Workspace',
    detail: 'Real-Time Kodak 500T 3D LUT Grading',
  },
  {
    tag: 'CINEMA STREAMING',
    icon: '🎬',
    metric: 'Direct-to-Fan 4K Premieres',
    detail: 'Global Indie & Studio Box Office Ecosystem',
  },
];

export default function FilmhookCinemaSplashIntro() {
  const { isDarkMode, toggleTheme } = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [impactTriggered, setImpactTriggered] = useState(false);
  const [cameraShake, setCameraShake] = useState(false);
  const [activeTelemetryIndex, setActiveTelemetryIndex] = useState(0);

  // Real-time 24fps Cinema Timecode State
  const [timecode, setTimecode] = useState('00:00:01:00');
  const frameCounterRef = useRef(24);

  // Interactive 3D Cursor Parallax
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Generate 16 radiant sparks radiating in a 360-degree circle
  const sparks = useMemo(() => {
    return Array.from({ length: 16 }).map((_, i) => {
      const angle = (i * (360 / 16) * Math.PI) / 180;
      const distance = 95 + (i % 4) * 32;
      return {
        id: i,
        x: Math.cos(angle) * distance,
        y: Math.sin(angle) * distance,
        size: 3 + (i % 3) * 2.2,
        delay: (i % 4) * 0.025,
      };
    });
  }, []);

  // Cycle telemetry data every 750ms
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setActiveTelemetryIndex((prev) => (prev + 1) % FILMHOOK_TELEMETRY.length);
    }, 750);
    return () => clearInterval(interval);
  }, [isVisible]);

  // Real-Time Running Cinema Timecode (24 FPS)
  useEffect(() => {
    if (!isVisible) return;
    const tcInterval = setInterval(() => {
      frameCounterRef.current += 1;
      const totalFrames = frameCounterRef.current;
      const frames = totalFrames % 24;
      const totalSeconds = Math.floor(totalFrames / 24);
      const seconds = totalSeconds % 60;
      const minutes = Math.floor(totalSeconds / 60) % 60;
      const hours = Math.floor(totalSeconds / 3600);

      const pad = (n) => String(n).padStart(2, '0');
      setTimecode(`${pad(hours)}:${pad(minutes)}:${pad(seconds)}:${pad(frames)}`);
    }, 41.6); // 1000ms / 24fps

    return () => clearInterval(tcInterval);
  }, [isVisible]);

  // Handle Interactive Cursor Parallax
  const handleMouseMove = useCallback((e) => {
    const { innerWidth, innerHeight } = window;
    const x = (e.clientX / innerWidth - 0.5) * 2; // -1 to +1
    const y = (e.clientY / innerHeight - 0.5) * 2;
    setMousePos({ x, y });
  }, []);

  const startIntro = useCallback(() => {
    setIsVisible(true);
    setImpactTriggered(false);
    setCameraShake(false);
    frameCounterRef.current = 24;

    // Impact timing aligns with logo touchdown at 820ms
    const impactTimer = setTimeout(() => {
      setImpactTriggered(true);
      setCameraShake(true);
      setTimeout(() => setCameraShake(false), 280);
    }, 820);

    // Smooth curtain lift transition at ~3.8s
    const exitTimer = setTimeout(() => {
      handleDismiss();
    }, 3800);

    return () => {
      clearTimeout(impactTimer);
      clearTimeout(exitTimer);
    };
  }, []);

  const handleDismiss = () => {
    try {
      sessionStorage.setItem('filmhook_splash_shown', 'true');
    } catch (e) {
      // Ignore storage errors in restricted contexts
    }
    setIsVisible(false);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const forceShow = urlParams.get('splash') === 'true' || urlParams.get('intro') === 'true';

    let alreadySeen = false;
    try {
      alreadySeen = sessionStorage.getItem('filmhook_splash_shown') === 'true';
    } catch (e) {
      alreadySeen = false;
    }

    if (!alreadySeen || forceShow) {
      startIntro();
    }

    window.replayFilmhookSplash = () => {
      startIntro();
    };

    return () => {
      delete window.replayFilmhookSplash;
    };
  }, [startIntro]);

  if (!isVisible) return null;

  // Dynamic Color Palette for White (Light) & Dark Modes
  const themeColors = isDarkMode
    ? {
        backdrop: 'radial-gradient(ellipse 85% 60% at 50% 45%, rgba(7, 164, 245, 0.18) 0%, rgba(3, 7, 18, 0.96) 65%, #020409 100%)',
        bgSolid: '#020409',
        coneGradient: 'linear-gradient(180deg, rgba(7, 164, 245, 0.45) 0%, rgba(7, 164, 245, 0.08) 55%, transparent 100%)',
        barBg: 'rgba(2, 4, 9, 0.92)',
        barBorder: 'rgba(255, 255, 255, 0.08)',
        hudMuted: 'rgba(255, 255, 255, 0.65)',
        textRec: 'rgba(255, 255, 255, 0.85)',
        timecode: '#07a4f5',
        titleGradient: 'linear-gradient(135deg, #ffffff 0%, #bae6fd 35%, #07a4f5 70%, #ffffff 100%)',
        subtitleColor: 'rgba(255, 255, 255, 0.82)',
        cardBg: 'rgba(7, 164, 245, 0.06)',
        cardBorder: 'rgba(7, 164, 245, 0.25)',
        badgeBg: 'rgba(7, 164, 245, 0.08)',
        badgeBorder: 'rgba(7, 164, 245, 0.28)',
        badgeText: '#38bdf8',
        sparkColor: '#38bdf8',
        shockBorder: 'rgba(7, 164, 245, 0.85)',
        shockGlow: 'rgba(7, 164, 245, 0.5)',
        logoFilterIdle: 'drop-shadow(0 0 45px rgba(7, 164, 245, 1)) drop-shadow(0 18px 55px rgba(0, 102, 255, 0.75)) drop-shadow(0 0 15px rgba(255, 255, 255, 0.5))',
        progressTrack: 'rgba(255, 255, 255, 0.1)',
        progressFill: 'linear-gradient(90deg, #07a4f5, #38bdf8)',
        skipBtnBg: 'rgba(255, 255, 255, 0.06)',
        skipBtnBorder: 'rgba(255, 255, 255, 0.15)',
        skipBtnColor: '#ffffff',
        reticleColor: 'rgba(7, 164, 245, 0.65)',
      }
    : {
        // Pristine Porcelain Studio White Mode
        backdrop: 'radial-gradient(ellipse 90% 70% at 50% 42%, rgba(7, 164, 245, 0.15) 0%, rgba(244, 248, 255, 0.96) 50%, #ffffff 100%)',
        bgSolid: '#f8fafc',
        coneGradient: 'linear-gradient(180deg, rgba(7, 164, 245, 0.28) 0%, rgba(7, 164, 245, 0.05) 55%, transparent 100%)',
        barBg: 'rgba(255, 255, 255, 0.92)',
        barBorder: 'rgba(15, 23, 42, 0.08)',
        hudMuted: '#64748b',
        textRec: '#0f172a',
        timecode: '#0284c7',
        titleGradient: 'linear-gradient(135deg, #0f172a 0%, #0369a1 40%, #0284c7 75%, #0f172a 100%)',
        subtitleColor: '#334155',
        cardBg: 'rgba(255, 255, 255, 0.85)',
        cardBorder: 'rgba(2, 132, 199, 0.25)',
        badgeBg: 'rgba(7, 164, 245, 0.08)',
        badgeBorder: 'rgba(7, 164, 245, 0.35)',
        badgeText: '#0284c7',
        sparkColor: '#0284c7',
        shockBorder: 'rgba(2, 132, 199, 0.8)',
        shockGlow: 'rgba(2, 132, 199, 0.35)',
        logoFilterIdle: 'drop-shadow(0 14px 28px rgba(7, 164, 245, 0.6)) drop-shadow(0 25px 50px rgba(2, 132, 199, 0.3)) drop-shadow(0 0 12px rgba(7, 164, 245, 0.4))',
        progressTrack: 'rgba(15, 23, 42, 0.08)',
        progressFill: 'linear-gradient(90deg, #0284c7, #38bdf8)',
        skipBtnBg: 'rgba(15, 23, 42, 0.05)',
        skipBtnBorder: 'rgba(15, 23, 42, 0.12)',
        skipBtnColor: '#0f172a',
        reticleColor: 'rgba(2, 132, 199, 0.65)',
      };

  const currentTelemetry = FILMHOOK_TELEMETRY[activeTelemetryIndex];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key="filmhook-cinema-splash"
          initial={{ opacity: 1 }}
          exit={{ 
            opacity: 0, 
            scale: 1.06, 
            filter: 'blur(10px)',
            transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } 
          }}
          onMouseMove={handleMouseMove}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999999,
            backgroundColor: themeColors.bgSolid,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            cursor: 'default',
            userSelect: 'none',
          }}
        >
          {/* Camera Impact Impulse Shake Wrapper */}
          <motion.div
            animate={{
              y: cameraShake ? [0, 5, -4, 2, 0] : 0,
              x: cameraShake ? [0, -3, 3, -1, 0] : 0,
            }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Ambient Radial Spotlight Background */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                background: themeColors.backdrop,
                transition: 'background 0.4s ease',
              }}
            />

            {/* Architectural Studio Grid Lines */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                opacity: isDarkMode ? 0.08 : 0.04,
                backgroundImage: `linear-gradient(${isDarkMode ? '#ffffff' : '#000000'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? '#ffffff' : '#000000'} 1px, transparent 1px)`,
                backgroundSize: '64px 64px',
                maskImage: 'radial-gradient(circle at 50% 50%, black 20%, transparent 75%)',
                WebkitMaskImage: 'radial-gradient(circle at 50% 50%, black 20%, transparent 75%)',
              }}
            />

            {/* Volumetric Cinema Projector Light Cone */}
            <motion.div
              initial={{ opacity: 0, scaleY: 0 }}
              animate={{ opacity: 0.45, scaleY: 1 }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
              style={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '65vw',
                height: '75vh',
                background: themeColors.coneGradient,
                clipPath: 'polygon(30% 0%, 70% 0%, 100% 100%, 0% 100%)',
                pointerEvents: 'none',
                filter: 'blur(25px)',
              }}
            />

            {/* Anamorphic Horizontal Laser Lens Flare Streak on Impact */}
            <AnimatePresence>
              {impactTriggered && (
                <motion.div
                  initial={{ opacity: 0, scaleX: 0 }}
                  animate={{ 
                    opacity: [0, 1, 0.7, 0], 
                    scaleX: [0, 1.4, 1.7, 2.0] 
                  }}
                  transition={{ duration: 0.95, ease: 'easeOut' }}
                  style={{
                    position: 'absolute',
                    top: '44%',
                    left: '-30%',
                    right: '-30%',
                    height: '2px',
                    background: isDarkMode
                      ? 'linear-gradient(90deg, transparent 0%, rgba(7, 164, 245, 0.3) 25%, rgba(7, 164, 245, 0.95) 48%, #ffffff 50%, rgba(7, 164, 245, 0.95) 52%, rgba(7, 164, 245, 0.3) 75%, transparent 100%)'
                      : 'linear-gradient(90deg, transparent 0%, rgba(2, 132, 199, 0.25) 25%, rgba(2, 132, 199, 0.9) 48%, #ffffff 50%, rgba(2, 132, 199, 0.9) 52%, rgba(2, 132, 199, 0.25) 75%, transparent 100%)',
                    boxShadow: isDarkMode
                      ? '0 0 35px 4px rgba(7, 164, 245, 0.8), 0 0 70px 8px rgba(0, 150, 255, 0.6)'
                      : '0 0 30px 4px rgba(2, 132, 199, 0.6), 0 0 60px 6px rgba(7, 164, 245, 0.4)',
                    pointerEvents: 'none',
                    zIndex: 8,
                  }}
                />
              )}
            </AnimatePresence>

            {/* Dual Concentric Impact Shockwaves */}
            <AnimatePresence>
              {impactTriggered && (
                <>
                  <motion.div
                    initial={{ scale: 0.2, opacity: 1 }}
                    animate={{ scale: 3.5, opacity: 0 }}
                    transition={{ duration: 1.1, ease: [0.15, 0.85, 0.35, 1] }}
                    style={{
                      position: 'absolute',
                      top: '44%',
                      left: '50%',
                      width: '180px',
                      height: '180px',
                      marginLeft: '-90px',
                      marginTop: '-90px',
                      borderRadius: '50%',
                      border: `2px solid ${themeColors.shockBorder}`,
                      boxShadow: `0 0 30px ${themeColors.shockGlow}, inset 0 0 25px ${themeColors.shockGlow}`,
                      pointerEvents: 'none',
                      zIndex: 6,
                    }}
                  />
                  <motion.div
                    initial={{ scale: 0.2, opacity: 0.9 }}
                    animate={{ scale: 2.7, opacity: 0 }}
                    transition={{ duration: 0.9, delay: 0.08, ease: [0.15, 0.85, 0.35, 1] }}
                    style={{
                      position: 'absolute',
                      top: '44%',
                      left: '50%',
                      width: '160px',
                      height: '160px',
                      marginLeft: '-80px',
                      marginTop: '-80px',
                      borderRadius: '50%',
                      border: `1.5px solid ${themeColors.sparkColor}`,
                      boxShadow: `0 0 20px ${themeColors.shockGlow}`,
                      pointerEvents: 'none',
                      zIndex: 6,
                    }}
                  />
                </>
              )}
            </AnimatePresence>

            {/* Radial Particle Sparks Exploding on Touchdown */}
            <AnimatePresence>
              {impactTriggered &&
                sparks.map((spark) => (
                  <motion.div
                    key={spark.id}
                    initial={{ x: 0, y: 0, opacity: 1, scale: 0 }}
                    animate={{
                      x: spark.x,
                      y: spark.y,
                      opacity: [1, 0.8, 0],
                      scale: [0, 1.4, 0.2],
                    }}
                    transition={{
                      duration: 0.75,
                      delay: spark.delay,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    style={{
                      position: 'absolute',
                      top: '44%',
                      left: '50%',
                      width: `${spark.size}px`,
                      height: `${spark.size}px`,
                      borderRadius: '50%',
                      backgroundColor: spark.id % 2 === 0 ? themeColors.sparkColor : '#ffffff',
                      boxShadow: `0 0 12px ${themeColors.sparkColor}`,
                      pointerEvents: 'none',
                      zIndex: 7,
                    }}
                  />
                ))}
            </AnimatePresence>

            {/* Central Stage: 3D Aerodynamic Diving "F" Logo + Ground Reflection */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 12,
              }}
            >
              {/* Hollywood Focus Reticle Brackets Snapping on Impact */}
              <AnimatePresence>
                {impactTriggered && (
                  <motion.div
                    initial={{ scale: 1.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    style={{
                      position: 'absolute',
                      top: '-20px',
                      left: '-32px',
                      right: '-32px',
                      bottom: '40px',
                      pointerEvents: 'none',
                      zIndex: 5,
                    }}
                  >
                    {/* Corner 1: Top Left */}
                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: 14, height: 14, borderTop: `2px solid ${themeColors.reticleColor}`, borderLeft: `2px solid ${themeColors.reticleColor}` }} />
                    {/* Corner 2: Top Right */}
                    <Box sx={{ position: 'absolute', top: 0, right: 0, width: 14, height: 14, borderTop: `2px solid ${themeColors.reticleColor}`, borderRight: `2px solid ${themeColors.reticleColor}` }} />
                    {/* Corner 3: Bottom Left */}
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: 14, height: 14, borderBottom: `2px solid ${themeColors.reticleColor}`, borderLeft: `2px solid ${themeColors.reticleColor}` }} />
                    {/* Corner 4: Bottom Right */}
                    <Box sx={{ position: 'absolute', bottom: 0, right: 0, width: 14, height: 14, borderBottom: `2px solid ${themeColors.reticleColor}`, borderRight: `2px solid ${themeColors.reticleColor}` }} />
                    
                    {/* Optical Center Crosshair Dots */}
                    <Box sx={{ position: 'absolute', top: '50%', left: -8, width: 6, height: '1px', bgcolor: themeColors.reticleColor }} />
                    <Box sx={{ position: 'absolute', top: '50%', right: -8, width: 6, height: '1px', bgcolor: themeColors.reticleColor }} />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Gravitational 3D Spiral Diving Trajectory with Cursor Parallax */}
              <motion.div
                initial={{
                  y: -500,
                  scale: 0.45,
                  rotateX: 45,
                  rotateY: 220,
                  rotateZ: -20,
                  opacity: 0,
                }}
                animate={{
                  y: [ -500, 0, -28, 0, -8, 0 ],
                  scale: [ 0.45, 1.34, 0.9, 1.06, 0.98, 1 ],
                  rotateX: [ 45, 0 + mousePos.y * -10, -6, 0 + mousePos.y * -10, 0, 0 + mousePos.y * -10 ],
                  rotateY: [ 220, 0 + mousePos.x * 12, 0, 0 + mousePos.x * 12, 0, 0 + mousePos.x * 12 ],
                  rotateZ: [ -20, 0, 3, 0, 0, 0 ],
                  opacity: [ 0, 1, 1, 1, 1, 1 ],
                }}
                transition={{
                  duration: 1.45,
                  times: [0, 0.56, 0.72, 0.84, 0.93, 1],
                  ease: [0.2, 0.9, 0.3, 1],
                }}
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  perspective: 1200,
                  transformStyle: 'preserve-3d',
                }}
              >
                {/* Luminous Light Trail Stream Following the Fall */}
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{
                    opacity: [0, 0.85, 0],
                    height: [0, 320, 0],
                  }}
                  transition={{ duration: 0.82, ease: 'easeIn' }}
                  style={{
                    position: 'absolute',
                    top: '-220px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80px',
                    background: `linear-gradient(180deg, transparent 0%, ${themeColors.shockGlow} 50%, #ffffff 100%)`,
                    filter: 'blur(15px)',
                    pointerEvents: 'none',
                  }}
                />

                {/* Dynamic Radial Halo Diffuser Behind Logo */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{
                    opacity: impactTriggered ? [0.4, 0.85, 0.5] : 0,
                    scale: impactTriggered ? [0.8, 1.35, 1.15] : 0.5,
                  }}
                  transition={{ duration: 1.4, repeat: Infinity, repeatType: 'reverse' }}
                  style={{
                    position: 'absolute',
                    inset: '-35px',
                    borderRadius: '50%',
                    background: isDarkMode
                      ? 'radial-gradient(circle, rgba(7, 164, 245, 0.7) 0%, rgba(0, 102, 255, 0.3) 50%, transparent 75%)'
                      : 'radial-gradient(circle, rgba(7, 164, 245, 0.35) 0%, rgba(2, 132, 199, 0.15) 50%, transparent 75%)',
                    filter: 'blur(32px)',
                    pointerEvents: 'none',
                  }}
                />

                {/* Diagonal Specular Sheen Laser Passing across Emblem */}
                <motion.div
                  initial={{ x: '-150%', opacity: 0 }}
                  animate={{
                    x: impactTriggered ? ['-150%', '200%'] : '-150%',
                    opacity: impactTriggered ? [0, 0.9, 0] : 0,
                  }}
                  transition={{ duration: 0.8, delay: 0.25, ease: 'easeInOut' }}
                  style={{
                    position: 'absolute',
                    top: '-20%',
                    bottom: '-20%',
                    width: '45px',
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.9) 50%, transparent 100%)',
                    transform: 'rotate(25deg)',
                    filter: 'blur(4px)',
                    pointerEvents: 'none',
                    zIndex: 3,
                  }}
                />

                {/* The Official Filmhook "F" Emblem */}
                <Box
                  component="img"
                  src={filmhookEmblem}
                  alt="Filmhook Official Emblem"
                  sx={{
                    width: { xs: 150, sm: 195, md: 235 },
                    height: 'auto',
                    position: 'relative',
                    filter: impactTriggered
                      ? themeColors.logoFilterIdle
                      : 'drop-shadow(0 -15px 25px rgba(7, 164, 245, 0.7))',
                    transition: 'filter 0.4s ease, transform 0.3s ease',
                    zIndex: 2,
                  }}
                />
              </motion.div>

              {/* Glossy Showroom Floor Reflection */}
              <motion.div
                initial={{ opacity: 0, scaleY: 0 }}
                animate={{
                  opacity: impactTriggered ? (isDarkMode ? 0.35 : 0.22) : 0,
                  scaleY: impactTriggered ? [-0.9, -1.05, -1] : 0,
                }}
                transition={{ duration: 0.6, delay: 0.1 }}
                style={{
                  position: 'relative',
                  marginTop: '-22px',
                  transform: 'scaleY(-1)',
                  maskImage: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.7) 0%, transparent 65%)',
                  WebkitMaskImage: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.7) 0%, transparent 65%)',
                  filter: isDarkMode ? 'blur(2px)' : 'blur(1.5px)',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              >
                <Box
                  component="img"
                  src={filmhookEmblem}
                  alt="Filmhook Floor Reflection"
                  sx={{
                    width: { xs: 150, sm: 195, md: 235 },
                    height: 'auto',
                  }}
                />
              </motion.div>

              {/* Letter-by-Letter Kinetic 3D Cascade for "FILMHOOK" */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: { xs: '6px', sm: '12px', md: '16px' },
                  mt: 2.5,
                }}
              >
                {['F', 'I', 'L', 'M', 'H', 'O', 'O', 'K'].map((char, index) => (
                  <motion.div
                    key={`${char}-${index}`}
                    initial={{ opacity: 0, y: 35, rotateX: 60, scale: 0.8 }}
                    animate={{
                      opacity: impactTriggered ? 1 : 0,
                      y: impactTriggered ? 0 : 35,
                      rotateX: impactTriggered ? 0 : 60,
                      scale: impactTriggered ? 1 : 0.8,
                    }}
                    transition={{
                      duration: 0.55,
                      delay: 0.15 + index * 0.045,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    style={{ perspective: 600 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Bricolage Grotesque", "Inter", sans-serif',
                        fontSize: { xs: '2rem', sm: '2.8rem', md: '3.4rem' },
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        background: themeColors.titleGradient,
                        backgroundSize: '200% auto',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textShadow: isDarkMode
                          ? '0 0 35px rgba(7, 164, 245, 0.4)'
                          : '0 4px 18px rgba(2, 132, 199, 0.25)',
                        lineHeight: 1,
                      }}
                    >
                      {char}
                    </Typography>
                  </motion.div>
                ))}
              </Box>

              {/* Tagline / Proposition */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{
                  opacity: impactTriggered ? 1 : 0,
                  y: impactTriggered ? 0 : 12,
                }}
                transition={{ duration: 0.6, delay: 0.45 }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: { xs: '0.72rem', sm: '0.85rem', md: '0.94rem' },
                    color: themeColors.subtitleColor,
                    letterSpacing: { xs: '0.14em', md: '0.24em' },
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    mt: 1.2,
                    textAlign: 'center',
                    transition: 'color 0.3s ease',
                  }}
                >
                  The Future of Cinema, Auditions & Post-Production
                </Typography>
              </motion.div>

              {/* Dynamic Live Filmhook Telemetry Feed (Rotating Live Status Pill) */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{
                  opacity: impactTriggered ? 1 : 0,
                  y: impactTriggered ? 0 : 15,
                }}
                transition={{ duration: 0.5, delay: 0.6 }}
                style={{
                  marginTop: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: { xs: 2, sm: 2.5 },
                    py: 0.8,
                    borderRadius: '100px',
                    background: themeColors.cardBg,
                    border: `1px solid ${themeColors.cardBorder}`,
                    backdropFilter: 'blur(16px)',
                    boxShadow: isDarkMode
                      ? '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 0 12px rgba(7, 164, 245, 0.15)'
                      : '0 8px 24px rgba(2, 132, 199, 0.12)',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {/* Live Pulse Beacon */}
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#10b981',
                      boxShadow: '0 0 10px #10b981',
                      animation: 'beaconPulse 1.4s infinite',
                      '@keyframes beaconPulse': {
                        '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                        '50%': { transform: 'scale(1.4)', opacity: 0.4 },
                      },
                    }}
                  />

                  {/* Cycling Animated Text */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentTelemetry.tag}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.25 }}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.72rem',
                          fontFamily: 'monospace',
                          fontWeight: 800,
                          color: themeColors.badgeText,
                          letterSpacing: '0.08em',
                        }}
                      >
                        {currentTelemetry.icon} {currentTelemetry.tag}:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.72rem',
                          fontFamily: '"Inter", sans-serif',
                          fontWeight: 600,
                          color: themeColors.subtitleColor,
                          letterSpacing: '0.04em',
                        }}
                      >
                        {currentTelemetry.metric}
                      </Typography>
                    </motion.div>
                  </AnimatePresence>
                </Box>
              </motion.div>

              {/* Dynamic Live Ecosystem Capabilities Dock */}
              <motion.div
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{
                  opacity: impactTriggered ? 1 : 0,
                  scale: impactTriggered ? 1 : 0.94,
                }}
                transition={{ duration: 0.5, delay: 0.72 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginTop: '16px',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                {[
                  { label: 'VERIFIED CASTING', metric: '14.8K+ Talent' },
                  { label: 'NEURAL DIFFUSION', metric: 'AI Storyboard V2.4' },
                  { label: 'CLOUD TIMELINE', metric: '4K DCI • 3D LUTs' },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      px: 1.8,
                      py: 0.5,
                      borderRadius: '8px',
                      background: themeColors.badgeBg,
                      border: `1px solid ${themeColors.badgeBorder}`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        borderColor: themeColors.timecode,
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.62rem',
                        fontFamily: 'monospace',
                        letterSpacing: '0.1em',
                        color: themeColors.hudMuted,
                        fontWeight: 700,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.72rem',
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 700,
                        color: themeColors.badgeText,
                      }}
                    >
                      {item.metric}
                    </Typography>
                  </Box>
                ))}
              </motion.div>
            </Box>
          </motion.div>

          {/* Top Cinema Letterbox Strip with Running Timecode & Cinema HUD */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: { xs: '44px', md: '54px' },
              background: themeColors.barBg,
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderBottom: `1px solid ${themeColors.barBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: 2, md: 4 },
              zIndex: 20,
              transition: 'background 0.3s ease, border-color 0.3s ease',
            }}
          >
            {/* Left: REC Indicator + Camera Profile */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    bgcolor: '#ef4444',
                    boxShadow: '0 0 10px #ef4444',
                    animation: 'recBlink 1s infinite',
                    '@keyframes recBlink': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.3 },
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    letterSpacing: '0.15em',
                    color: themeColors.textRec,
                    fontWeight: 700,
                  }}
                >
                  REC
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.72rem',
                  letterSpacing: '0.08em',
                  color: themeColors.hudMuted,
                  display: { xs: 'none', md: 'block' },
                }}
              >
                [4K DCI • 2.39:1 • 24.000 FPS • ISO 800]
              </Typography>
            </Box>

            {/* Right: Dynamic Live Timecode + Mode Switch + Skip Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* Dynamic Running Timecode Counter */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.8,
                  px: 1.2,
                  py: 0.3,
                  borderRadius: '6px',
                  background: isDarkMode ? 'rgba(7, 164, 245, 0.1)' : 'rgba(2, 132, 199, 0.08)',
                  border: `1px solid ${themeColors.cardBorder}`,
                }}
              >
                <SensorsRoundedIcon sx={{ fontSize: '0.9rem', color: themeColors.timecode }} />
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.76rem',
                    letterSpacing: '0.14em',
                    color: themeColors.timecode,
                    fontWeight: 800,
                  }}
                >
                  TC {timecode}
                </Typography>
              </Box>

              {/* Theme Toggle (White / Dark Mode) */}
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTheme();
                }}
                size="small"
                startIcon={
                  isDarkMode ? (
                    <LightModeRoundedIcon sx={{ fontSize: '0.95rem !important', color: '#fbbf24' }} />
                  ) : (
                    <DarkModeRoundedIcon sx={{ fontSize: '0.95rem !important', color: '#0284c7' }} />
                  )
                }
                sx={{
                  color: themeColors.skipBtnColor,
                  bgcolor: themeColors.skipBtnBg,
                  border: `1px solid ${themeColors.skipBtnBorder}`,
                  borderRadius: '20px',
                  px: 1.4,
                  py: 0.3,
                  fontSize: '0.72rem',
                  textTransform: 'none',
                  letterSpacing: '0.04em',
                  fontWeight: 600,
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: isDarkMode ? 'rgba(7, 164, 245, 0.2)' : 'rgba(2, 132, 199, 0.12)',
                    borderColor: themeColors.timecode,
                  },
                }}
              >
                {isDarkMode ? 'White Mode' : 'Dark Mode'}
              </Button>

              {/* Skip Intro Button */}
              <Button
                onClick={handleDismiss}
                size="small"
                startIcon={<CloseRoundedIcon sx={{ fontSize: '1rem !important' }} />}
                sx={{
                  color: themeColors.skipBtnColor,
                  bgcolor: themeColors.skipBtnBg,
                  border: `1px solid ${themeColors.skipBtnBorder}`,
                  borderRadius: '20px',
                  px: 1.8,
                  py: 0.3,
                  fontSize: '0.72rem',
                  textTransform: 'none',
                  letterSpacing: '0.06em',
                  fontWeight: 600,
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: isDarkMode ? 'rgba(7, 164, 245, 0.2)' : 'rgba(2, 132, 199, 0.12)',
                    borderColor: themeColors.timecode,
                  },
                }}
              >
                Skip Intro
              </Button>
            </Box>
          </Box>

          {/* Bottom Cinema Letterbox Strip with Real-Time Audio EQ Visualizer */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: { xs: '44px', md: '54px' },
              background: themeColors.barBg,
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderTop: `1px solid ${themeColors.barBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: 2, md: 4 },
              zIndex: 20,
              transition: 'background 0.3s ease, border-color 0.3s ease',
            }}
          >
            {/* Audio Spectrum Analyzer Visualizer */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <GraphicEqRoundedIcon sx={{ fontSize: '1.1rem', color: themeColors.timecode }} />
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '16px' }}>
                {[65, 90, 45, 80, 100, 55, 85, 40, 75, 95].map((height, i) => (
                  <motion.div
                    key={i}
                    animate={{
                      height: [`${height * 0.3}%`, `${height}%`, `${height * 0.5}%`],
                    }}
                    transition={{
                      duration: 0.4 + (i % 3) * 0.15,
                      repeat: Infinity,
                      repeatType: 'reverse',
                      ease: 'easeInOut',
                    }}
                    style={{
                      width: '3px',
                      background: themeColors.sparkColor,
                      borderRadius: '2px',
                    }}
                  />
                ))}
              </Box>
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                  letterSpacing: '0.1em',
                  color: themeColors.hudMuted,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                AUDIO: 5.1 DOLBY SURROUND • 48 kHz
              </Typography>
            </Box>

            {/* Progress Bar + Ecosystem Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                  letterSpacing: '0.12em',
                  color: themeColors.subtitleColor,
                  fontWeight: 600,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                INITIALIZING FILMHOOK ECOSYSTEM
              </Typography>

              <Box
                sx={{
                  width: { xs: '110px', sm: '180px' },
                  height: '4px',
                  background: themeColors.progressTrack,
                  borderRadius: '2px',
                  overflow: 'hidden',
                }}
              >
                <motion.div
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 3.6, ease: 'linear' }}
                  style={{
                    height: '100%',
                    background: themeColors.progressFill,
                    boxShadow: `0 0 10px ${themeColors.sparkColor}`,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
